import { OfflineRecordingCapDialog } from './OfflineRecordingCapDialog.tsx';
import { OfflineRecordingLeaveDialog } from './OfflineRecordingLeaveDialog.tsx';
import { OfflineRecordingLiveDialog } from './OfflineRecordingLiveDialog.tsx';
import { OfflineRecordingReplaceDialog } from './OfflineRecordingReplaceDialog.tsx';
import { OfflineRecordingResumeDialog } from './OfflineRecordingResumeDialog.tsx';
import { OfflineRecordingStartDialog } from './OfflineRecordingStartDialog.tsx';
import type { MeetingRecording } from './useMeetingRecording.ts';

export interface MeetingRecordingDialogsProps {
  recording: MeetingRecording;
}

/**
 * The moments of a microphone recording that need an answer: which
 * microphone, whether to pick an interrupted one back up, three hours in
 * whether anybody is still there, and whether leaving may end it.
 */
export function MeetingRecordingDialogs({ recording }: MeetingRecordingDialogsProps) {
  const { recorder, ask, setAsk, leaving } = recording;

  return (
    <>
      <OfflineRecordingReplaceDialog
        open={ask === 'replace'}
        onConfirm={() => setAsk('setup')}
        onCancel={() => setAsk(null)}
      />

      <OfflineRecordingStartDialog
        open={ask === 'setup'}
        pending={recorder.starting}
        failed={recorder.startFailed}
        onStart={recording.start}
        onCancel={() => setAsk(null)}
      />

      {/* Steps aside for the three-hour question, which is about the same recording. */}
      <OfflineRecordingLiveDialog
        open={
          recorder.state === 'recording' && !recording.minimized && !recorder.capReached
        }
        elapsed={recorder.elapsed}
        startedAt={recorder.startedAt}
        device={recorder.deviceLabel}
        chunkLost={recorder.chunkLost}
        pending={recording.pending}
        onStop={recording.stop}
        onMinimize={() => recording.setMinimized(true)}
      />

      <OfflineRecordingLeaveDialog
        open={leaving.status === 'blocked'}
        onStay={() => leaving.reset?.()}
        onLeave={recording.leave}
      />

      <OfflineRecordingResumeDialog
        open={ask === 'resume'}
        pending={recorder.starting}
        failed={recorder.startFailed}
        onConfirm={recording.resume}
        onCancel={() => setAsk(null)}
      />

      <OfflineRecordingCapDialog
        open={recorder.capReached}
        onContinue={recorder.keepGoing}
        onStop={recording.stop}
      />
    </>
  );
}
